// Libraries
import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';

// Interfaces
import { ImageMask, Model } from 'src/app/interfaces/api/model';
import { CalibrationResult } from 'src/app/interfaces/shared/calibration';

export type EditorMode = 'none' | 'mask' | 'calibration' | 'transform';
export type ImageAction = | { type: 'upload'; seq: number } | { type: 'delete'; seq: number };

@Injectable({ providedIn: 'root' })
export class MapStore {
    // Signals
    private readonly _currentModel: WritableSignal<Model | null> = signal(null);
    public readonly currentModel: Signal<Model | null> = this._currentModel.asReadonly();

    private readonly _map: WritableSignal<GoogleMap | null> = signal(null);
    public readonly map: Signal<GoogleMap | null> = this._map.asReadonly();

    private readonly _imageInfo = signal<{ src: string; width: number; height: number } | null>(null);
    public readonly imageInfo = this._imageInfo.asReadonly();

    private readonly _masks: WritableSignal<ImageMask[]> = signal([]);
    public readonly masks: Signal<ImageMask[]> = this._masks.asReadonly();

    private readonly _editorMode: WritableSignal<EditorMode> = signal('none');
    public readonly editorMode: Signal<EditorMode> = this._editorMode.asReadonly();

    private readonly _isDirty: WritableSignal<boolean> = signal(false);
    public readonly isDirty: Signal<boolean> = this._isDirty.asReadonly();

    private _imageAction = signal<ImageAction | null>(null);
    public readonly imageAction = this._imageAction.asReadonly();

    // Variables
    private _imageActionSeq = 0;

    // ======================= Setters / Getters ======================= //

    setMap(map: GoogleMap) {
        this._map.set(map);
    }

    setCurrentModel(model: Model | null) {
        this._currentModel.set(model);

        if (model?.imageTransform?.masks) {
            this._masks.set(
                model.imageTransform.masks.map(m => ({
                    mode: m.mode,
                    rect: { ...m.rect }
                }))
            );
        } else {
            this._masks.set([]);
        }
    }

    setEditorMode(mode: EditorMode): void {
        this._editorMode.set(mode);
    }

    // =================== Image Info Management =================== //

    async loadImageInfo(src: string): Promise<{ width: number; height: number }> {
        const current = this._imageInfo();

        if (current?.src === src) {
            return { width: current.width, height: current.height };
        }

        const size = await this.computeImageSize(src);

        this._imageInfo.set({
            src,
            width: size.width,
            height: size.height,
        });

        return size;
    }

    private computeImageSize(src: string): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                resolve({ width: img.width, height: img.height });
            };

            img.onerror = reject;

            img.src = src;
        });
    }

    markClean(): void {
        this._isDirty.set(false);
    }

    saveCalibration(result: CalibrationResult): void {
        const model = this._currentModel();
        if (!model) return;

        const updatedModel: Model = {
            ...model,
            mapCoord: result.mapCoord,
            mapCenter: result.center,
            imageTransform: {
                ...(model.imageTransform ?? {}),
                angle: result.angle,
                opacity: model.imageTransform?.opacity ?? 1,
            },
            mapImageAngle: result.angle // synchro legacy
        };

        this._currentModel.set(updatedModel);
        this._isDirty.set(true);
    }

    // =================== Masked Image Management =================== //

    saveMasks(masks: ImageMask[]): void {
        this._masks.set(
            masks.map(m => ({
                mode: m.mode,
                rect: { ...m.rect }
            }))
        );

        const model = this._currentModel();
        if (!model) return;

        const updatedModel: Model = {
            ...model,
            imageTransform: {
                ...(model.imageTransform ?? {}),
                masks: [...masks]
            }
        };

        this._currentModel.set(updatedModel);
        this._isDirty.set(true);
    }

    addMask(mask: ImageMask): void {
        const current = this._masks();
        this.saveMasks([...current, mask]);
    }

    resetMasks(): void {
        this.saveMasks([]);
    }

    // =================== Image Upload/Delete Management =================== //

    notifyImageUploaded(): void {
        this._isDirty.set(true);
        this._imageAction.set({ type: 'upload', seq: ++this._imageActionSeq });
    }

    notifyImageDeleted(): void {
        const model = this._currentModel();
        if (!model) return;

        const updatedModel: Model = {
            ...model,
            mapImage: "",
            mapImageSrc: "",
            imageTransform: {
                angle: 0,
                opacity: 1,
                masks: []
            },
            mapImageAngle: 0
        };

        this._currentModel.set(updatedModel);
        this._imageInfo.set(null); 
        this._isDirty.set(true);
        this._imageAction.set({ type: 'delete', seq: ++this._imageActionSeq });
    }

    deleteImageFromModel(): void {
        this.notifyImageDeleted();
    }
}